import React from 'react';
import { Field, Formik } from 'formik';
import * as Yup from 'yup';
import { Button, Datepicker } from '@brighterion/br-ui';
import { trackEvent, EventTypes, EventNames } from '@brighterion/insights';
import { useQueryClient } from '@tanstack/react-query';
import {
    getRangeOfRelativeDates,
    MS_PER_DAY,
    RQ_GET_RULE_TEST_RESULTS,
    RT_DURATION,
    RT_END_DATE,
    RT_RANGE
} from 'Utils';
import useRuleTester from 'Hooks/query-hooks/global-rules/useRuleTester';
import { useTemplateStore } from 'Hooks/zustand/useTemplateStore';
import { useTranslation } from 'react-i18next';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import { useSelector } from 'react-redux';
import { getUser } from 'Selectors';
import { FOCUS_VIEW } from 'Common/enums';
import styles from './RuleTesterForm.css';

type RuleTesterFormProps = {
    formikProps?: any;
    setRuleTestId: Function;
    parameterData?: UseGetParametersResponse;
    isRuleTestRunning: boolean;
    setIsRuleTestRunning: Function;
    conditions?: any;
    merchantId: string;
    tenantGroupId: string;
    ruleSetId: string;
    mode: 'add' | 'edit' | 'read';
    rulesType?: RulesType;
    overrideLevel?: OverrideLevel;
    outputDescriptor: string;
};

const RuleTesterForm = ({
    conditions,
    formikProps,
    isRuleTestRunning,
    merchantId,
    mode,
    parameterData,
    ruleSetId,
    setIsRuleTestRunning,
    setRuleTestId,
    tenantGroupId,
    rulesType,
    overrideLevel,
    outputDescriptor = ''
}: RuleTesterFormProps) => {
    const { t } = useTranslation();
    const { addModal } = useReduxModal();
    const queryClient = useQueryClient();
    const { tenantKey } = useSelector(getUser);
    const { template } = useTemplateStore.getState();
    const ruleTesterSchema = Yup.object().shape({
        start: Yup.date(),
        end: Yup.date()
    });
    const {
        isValid,
        values: { conditions: formikConditions, actions, ruleId, score }
    } = formikProps;
    const disableSave = !isValid;
    const allowedDateRange = getRangeOfRelativeDates(RT_RANGE, RT_END_DATE, new Date(), false);
    const latestAllowedDate = allowedDateRange[allowedDateRange.length - 1];
    const initialValues = {
        start: new Date(latestAllowedDate.setHours(0, 0, 0, 0)),
        end: new Date(latestAllowedDate.setHours(23, 59, 0, 0))
    };
    const getActions = () => {
        const values = [''];
        let operator = 'REJECT';
        switch (outputDescriptor) {
            case FOCUS_VIEW.ACCEPT:
                operator = 'ACCEPT';
                break;
            case FOCUS_VIEW.REVIEW:
                operator = 'Review';
                break;
            default:
                operator = 'UPDATE_SCORE_BY';
                values[0] = score || '';
        }
        const templatedActions = [{ values, actionId: 0, operator }];
        return templatedActions;
    };
    const updatedActions = Array.isArray(actions) ? actions : getActions();
    const onSuccess = (_data: any) => {
        setRuleTestId(_data.id);
        queryClient.invalidateQueries([
            RQ_GET_RULE_TEST_RESULTS,
            ruleId,
            tenantKey,
            merchantId,
            tenantGroupId,
            ruleSetId
        ]);
    };

    const onError = (err: any) => {
        setIsRuleTestRunning(false);
        addModal({
            id: 'update-rule-test-error',
            status: 'error',
            title: t('Unable to run rule test'),
            content: (
                <>
                    {t('updateRuleTestErrorSpecific', {
                        errMsg: err?.data?.error?.message ?? err?.data?.error?.msg?.error
                    })}
                </>
            ),
            cancelText: t('Close')
        });
    };

    const ruleTesterMutation = useRuleTester({
        mutationOptions: {
            onSuccess,
            onError
        }
    });

    const handleTrackEvent = () => {
        let clickIdentifier = '';
        let text = '';
        if (mode === 'add') {
            clickIdentifier = 'New Simple Rule - Start Test';
            text = 'Start New Simple Global Rule Test';
            if (rulesType === 'merchantOverrides') {
                clickIdentifier = 'New Subject Override - Start Test';
                text = 'Start New Subject Override Rule Test';
            } else if (overrideLevel === 'tenantGroup') {
                clickIdentifier = 'New Tenant Group Override - Start Test';
                text = 'Start New Tenant Group Override Rule Test';
            }
        } else if (mode === 'edit') {
            clickIdentifier = 'Edit Simple Rule - Start Test';
            text = 'Start Global Rule Test';
            if (rulesType === 'merchantOverrides') {
                clickIdentifier = 'Edit Subject Override - Start Test';
                text = 'Start Subject Override Rule Test';
            } else if (overrideLevel === 'tenantGroup') {
                clickIdentifier = 'Edit Tenant Group Override - Start Test';
                text = 'Start Tenant Group Override Rule Test';
            }
        }

        if (!clickIdentifier || !text) {
            return;
        }

        trackEvent({
            eventType: EventTypes.track,
            eventName: EventNames.click,
            payload: {
                clickIdentifier,
                text
            }
        });
    };

    const getDateString = (currentDate: Date) => {
        const year = currentDate.getFullYear();
        const month = (currentDate.getMonth() + 1).toLocaleString('en-US', {
            minimumIntegerDigits: 2,
            useGrouping: false
        });
        const date = currentDate.getDate().toLocaleString('en-US', {
            minimumIntegerDigits: 2,
            useGrouping: false
        });
        return `${year}-${month}-${date}`;
    };

    const onRunTest = ({ start, end }: { start: Date; end: Date }) => {
        const transformedConditions = formikConditions
            ? formikConditions.map(
                  ({ parameterField, operatorField, valueField, readOnly, conditionId }: CustomConditionObj) => ({
                      conditionId,
                      fieldName: parameterField.name[0],
                      tppType: parameterData?.fieldNameHashmap[parameterField.name[0]]?.tppType,
                      operator: operatorField[0],
                      values: typeof valueField !== 'object' ? [String(valueField)] : valueField,
                      readOnly
                  })
              )
            : conditions;
        setIsRuleTestRunning(true);
        const startDt = new Date(start.setHours(0, 0, 0, 0));
        const endDt = new Date(end.setHours(23, 59, 0, 0));
        ruleTesterMutation.mutate({
            startDt,
            endDt,
            ruleId,
            conditions: transformedConditions,
            actions: updatedActions,
            merchantId,
            tenantGroupId,
            ruleSetId,
            clientId: template?.clientId || tenantKey,
            startDateString: getDateString(startDt),
            endDateString: getDateString(endDt)
        });
        handleTrackEvent();
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={ruleTesterSchema}
            onSubmit={(formValues) => onRunTest(formValues)}
        >
            {({ values, setFieldValue }) => {
                const { start, end } = values;
                const rangeCalculation = Math.ceil(Math.abs(Number(latestAllowedDate) - Number(start)) / MS_PER_DAY);
                const allowedEndDateRange = getRangeOfRelativeDates(
                    RT_END_DATE,
                    rangeCalculation <= RT_DURATION - 1 ? rangeCalculation : RT_DURATION,
                    values.start,
                    true
                );
                return (
                    <div className={styles.ruleTesterForm}>
                        <div>
                            <div className={styles.datePickerLabel}>{t('Start Date')}: </div>

                            <Field name='start'>
                                {() => (
                                    <Datepicker
                                        name={['start-date']}
                                        type='single'
                                        value={values.start}
                                        size='sm'
                                        handleChange={(value) => {
                                            setFieldValue('start', value);
                                            setFieldValue('end', null);
                                        }}
                                        editable
                                        includeDates={allowedDateRange}
                                        maxDate={latestAllowedDate}
                                        disabled={isRuleTestRunning}
                                    />
                                )}
                            </Field>
                        </div>

                        <div>
                            <div className={styles.datePickerLabel}>{t('End Date')}: </div>

                            <Field name='end'>
                                {() => (
                                    <Datepicker
                                        name={['end-date']}
                                        type='single'
                                        value={end}
                                        size='sm'
                                        handleChange={(value) => {
                                            setFieldValue('end', value);
                                        }}
                                        editable
                                        includeDates={allowedEndDateRange}
                                        maxDate={latestAllowedDate}
                                        disabled={isRuleTestRunning}
                                    />
                                )}
                            </Field>
                        </div>

                        <Button
                            dataTestId='start-test'
                            variant='contextual-secondary'
                            color='primary'
                            size='medium'
                            onClick={() => {
                                onRunTest(values);
                            }}
                            onKeyDown={() => {
                                onRunTest(values);
                            }}
                            disabled={disableSave || !start || !end || isRuleTestRunning || !ruleId}
                        >
                            {t('Start Test')}
                        </Button>
                    </div>
                );
            }}
        </Formik>
    );
};

export default React.memo(RuleTesterForm);
